/**
 * BOSS Reports — the frame around the shortcut panel and the field editor.
 *
 * One component draws both, in either of two places: in the page layout as it always was, or
 * floating over the grid. Keeping it in one place is what stops the two panels drifting into
 * different affordances for the same idea — and the header it adds is where the pop-out control
 * lives, so every panel that can float says so in the same way.
 *
 * A floating panel is NOT a dimming modal, and the difference is deliberate. It is a working
 * surface: the grid stays fully visible and fully interactive underneath, you can drag fields in
 * the panel and watch the grid redraw behind it, and nothing is greyed out. So it takes none of
 * the modal treatment — no backdrop, no focus trap, no scroll lock — and it stays open until it is
 * explicitly closed rather than dismissing itself on an outside click. Escape closes it only while
 * focus is inside it, which is why that listener is on the panel and not on the document: a
 * global one would fight the grid's context menu and close a panel the user was not looking at.
 *
 * The float layer is rendered inside the grid pane rather than over the page, so a floating panel
 * can never cover the report's title or its buttons.
 */
import { useEffect, useRef } from 'react'
import type { JSX, KeyboardEvent as ReactKeyboardEvent, ReactNode } from 'react'

import type { BossPanelMode } from '../../lib/bossPanels'

export type BossPanelProps = {
    /** Shown in the header, and names the panel for assistive tech. */
    title: string
    mode: BossPanelMode
    /** Switch between the layout and a floating panel. */
    onMode: (mode: BossPanelMode) => void
    /** Hide the panel. The toolbar's own toggle brings it back. */
    onClose: () => void
    /**
     * Distinguishes the two panels for layout — 'shortcuts' keeps the docked column's styling,
     * 'fields' spans the grid pane. Also picks where the floating version sits.
     */
    variant: 'shortcuts' | 'fields'
    children: ReactNode
}

/** The pop-out / dock control. Its icon and wording say where the panel is GOING, not where it is. */
function ModeButton({ mode, onMode }: { mode: BossPanelMode; onMode: (mode: BossPanelMode) => void }): JSX.Element {
    const popping = mode === 'docked'
    return (
        <button
            type="button"
            className="boss-panel-btn"
            aria-pressed={!popping}
            title={popping
                ? 'Float this panel over the grid, freeing the space it takes up'
                : 'Put this panel back in the page layout'}
            onClick={() => onMode(popping ? 'popup' : 'docked')}
        >
            <span aria-hidden="true">{popping ? '⧉' : '⊟'}</span>
            <span className="sr-only">{popping ? 'Float this panel' : 'Dock this panel'}</span>
        </button>
    )
}

export default function BossPanel({
    title, mode, onMode, onClose, variant, children,
}: BossPanelProps): JSX.Element {
    const floating = mode === 'popup'
    const panelRef = useRef<HTMLDivElement | null>(null)

    // Moving a panel to the float layer moves its DOM, so anything focused inside it is dropped and
    // focus falls back to <body> — leaving a keyboard user nowhere. The panel takes focus itself,
    // which also puts the Escape handler below in reach immediately.
    useEffect(() => {
        if (!floating) return
        panelRef.current?.focus()
    }, [floating])

    const handleKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
        if (!floating || event.key !== 'Escape') return
        // Only when the key was not already claimed by something inside the panel — a select being
        // closed, say. Those call preventDefault when they handle it.
        if (event.defaultPrevented) return
        event.stopPropagation()
        onClose()
    }

    return (
        <div
            ref={panelRef}
            // The docked classes are the ones the page has always styled: keeping them means the
            // docked panel is byte-for-byte the layout it was before any of this existed.
            className={floating
                ? `boss-panel-float boss-panel-float-${variant}`
                : `boss-panel-docked boss-panel-docked-${variant}`}
            role="group"
            aria-label={title}
            // Focusable only while floating, and only as a container — it is not a tab stop in the
            // page's normal flow.
            tabIndex={floating ? -1 : undefined}
            onKeyDown={handleKeyDown}
        >
            <div className="boss-panel-head">
                <span className="boss-panel-title">{title}</span>
                <span className="boss-panel-head-actions">
                    <ModeButton mode={mode} onMode={onMode} />
                    <button
                        type="button"
                        className="boss-panel-btn"
                        title={`Hide ${title.toLowerCase()}`}
                        onClick={onClose}
                    >
                        <span aria-hidden="true">✕</span>
                        <span className="sr-only">Hide {title}</span>
                    </button>
                </span>
            </div>

            <div className="boss-panel-body">{children}</div>
        </div>
    )
}
